import * as React from 'react';
import classnames from 'classnames';

import Button from '@components/Button';
import ModalBase from '@components/ModalBase';

import './styles.scss';

interface IClippedTextProps {
  text: string;
  direction?: ClippedTextDirection;
  clipAfter?: number;
  clipBefore?: number;
  expandText?: string;
  collapseText?: string;
  isInnerHTML?: boolean;
  showHtml?: boolean;
  showModal?: boolean;
  disableSSR?: boolean;
  color?: boolean;
}

interface IClippedTextState {
  showFulltext: boolean;
  toggleText: string;
  isViewModalOpen: boolean;
  prevText: string;
}

type HTMLSpanProps = React.DetailedHTMLProps<React.HTMLAttributes<HTMLSpanElement>, HTMLSpanElement>;

export default class ClippedText extends React.Component<IClippedTextProps, IClippedTextState> {
  public static defaultProps = {
    text: '',
    direction: ClippedTextDirection.RTL,
    clipAfter: null,
    clipBefore: null,
    expandText: 'more',
    collapseText: 'less',
    color: false,
  };

  constructor(props: IClippedTextProps) {
    super(props);

    this.state = {
      showFulltext: !(this.props.clipAfter && (this.props.text || '').length < this.props.clipAfter),
      toggleText: this.props.expandText,
      isViewModalOpen: false,
      prevText: '',
    };
  }

  static getDerivedStateFromProps(
    nextProps: IClippedTextProps,
    prevState: IClippedTextState
  ): Partial<IClippedTextState> {
    if (prevState.prevText !== nextProps.text && prevState.showFulltext) {
      return {
        prevText: nextProps.text,
        showFulltext: nextProps.clipAfter && (nextProps.text || '').length < nextProps.clipAfter,
        toggleText: nextProps.expandText,
      };
    }

    return null;
  }

  renderText = () => {
    const { text, direction, clipAfter, clipBefore, isInnerHTML, showHtml } = this.props;
    let textContent = text;

    if (this.state.showFulltext) {
      return textContent;
    }
    if (isInnerHTML) {
      const doc = new DOMParser().parseFromString(text, 'text/html');
      textContent = showHtml ? doc.body.innerHTML : doc.body.innerText;
    }
    if (direction === ClippedTextDirection.RTL) {
      return clipAfter
        ? `${textContent.length > clipAfter ? `${textContent.substring(0, clipAfter)}...` : textContent}`
        : textContent;
    } else {
      return clipBefore
        ? `${textContent.length < clipBefore ? textContent : `...${textContent.substr(clipBefore)}`}`
        : textContent;
    }
  };

  toggleViewModal = () => {
    this.setState((prevState) => ({
      isViewModalOpen: !prevState.isViewModalOpen,
    }));
  };

  toggleTextLength = (e?: React.SyntheticEvent<HTMLButtonElement>) => {
    this.setState((prevState) => ({
      showFulltext: !prevState.showFulltext,
      toggleText: prevState.showFulltext ? this.props.expandText : ` ${this.props.collapseText}`,
    }));
  };

  handleToggleClick = () => {
    if (this.props.showModal) {
      this.toggleViewModal();
    } else {
      this.toggleTextLength();
    }
  };

  renderViewModal = () => {
    const disclaimerText = this.props.text;
    const modalFooter = (
      <div className={classnames('flex-row--middle')}>
        <Button onClick={this.toggleViewModal} bsStyle={ButtonStyle.DANGER}>
          Close
        </Button>
      </div>
    );

    return (
      <ModalBase
        show={this.state.isViewModalOpen}
        footerElement={modalFooter}
        bodyElement={disclaimerText}
        titleElement={'Note'}
        onClose={this.toggleViewModal}
        disableSSR={this.props.disableSSR}
      />
    );
  };

  render() {
    const { isInnerHTML, showHtml, color } = this.props;
    const spanProps: HTMLSpanProps = {};
    if (isInnerHTML && (this.state.showFulltext || showHtml)) {
      spanProps.dangerouslySetInnerHTML = { __html: this.renderText() };
    } else {
      spanProps.children = this.renderText();
    }

    const classNames = color ? ['clip-text', 'color-change'] : ['clip-text'];

    return (
      <div>
        <span className={classnames('clipped-text')} {...spanProps} />
        {(this.props.text || '').length > this.props.clipAfter && (
          <button 
            type="button"
            className={classnames(classNames)} 
            onClick={this.toggleTextLength}
            aria-label={this.state.showFulltext ? 'Show less text' : 'Show more text'}
            aria-expanded={this.state.showFulltext}
          >
            {this.state.toggleText}
          </button>
        )}
        {this.state.isViewModalOpen && this.renderViewModal()}
      </div>
    );
  }
}
